<template>
	<div id="directory-page" data-aos="fade">
		<!-- Hero -->
		<section class="top-section">
			<div class="container">
				<div class="row">
					<div class="col-12 col-lg-10 col-xl-8 mx-auto">
						<p class="hero-eyebrow" data-aos="fade-up">Directorio</p>
						<h1 class="hero-title" data-aos="fade-up" data-aos-delay="50">Abogados verificados</h1>
						<p class="hero-lead" data-aos="fade-up" data-aos-delay="100">
							Encuentra al profesional perfecto para tu caso entre nuestra red de expertos verificados.
						</p>
					</div>
				</div>
			</div>
		</section>

		<section class="content-section">
			<div class="container">
				<!-- Buscador IA -->
				<div class="search-card mb-4" data-aos="fade-up">
					<div class="ai-badge mb-3">
						<i class="fa-regular fa-robot"></i>
						<i class="fa-regular fa-sparkles ai-sparkle"></i>
						<span>Buscador potenciado por IA</span>
					</div>

					<div class="search-input-wrap position-relative">
						<i class="fa-regular fa-magnifying-glass search-icon"></i>
						<b-form-input
							v-model="searchTerm"
							type="search"
							placeholder="¿Qué abogado/firma necesitas?"
							class="search-input"
							autocomplete="off"
							@input="handleSearchChange"
							@focus="onSearchFocus"
							@blur="onSearchBlur"
						></b-form-input>
						<div v-if="isTyping" class="typing-indicator">
							<i class="fa-regular fa-sparkles fa-spin"></i>
							<span>IA analizando...</span>
						</div>

						<div
							v-if="showAutocomplete && autocompleteResults.length"
							class="autocomplete-dropdown"
						>
							<button
								v-for="(suggestion, index) in autocompleteResults"
								:key="index"
								type="button"
								class="autocomplete-item"
								@mousedown.prevent="handleAutocompleteSelect(suggestion)"
							>
								<i class="fa-regular fa-sparkles"></i>
								<span>{{ suggestion }}</span>
							</button>
						</div>
					</div>

					<p class="search-hint mb-0">
						<i class="fa-regular fa-robot"></i>
						Nuestra IA analiza tu consulta para encontrar los profesionales más adecuados para tu caso
					</p>
				</div>

				<!-- Filtros -->
				<template v-if="shouldShowResults">
					<div class="results-ai-label mb-3">
						<i class="fa-regular fa-sparkles"></i>
						<span>Resultados optimizados por IA</span>
					</div>

					<div class="filters-row mb-4">
						<b-form-checkbox v-model="showFirmas" class="mr-4">
							Firmas
						</b-form-checkbox>
						<b-form-checkbox v-model="showParticulares" class="mr-4">
							Particulares
						</b-form-checkbox>
						<b-form-input
							v-model="ciudadFilter"
							type="text"
							placeholder="Filtrar por ciudad"
							class="city-filter"
						></b-form-input>
					</div>

					<!-- Resultados -->
					<div class="row">
						<div
							v-for="profesional in filteredProfesionales"
							:key="profesional.id"
							class="col-md-6 col-lg-4 mb-4"
						>
							<router-link
								:to="'/directorio/' + profesional.id"
								class="profesional-card"
							>
								<h3 class="profesional-name">
									{{ fullName(profesional) }}
								</h3>

								<div class="avatar-wrap">
									<img
										v-if="profesional.avatar"
										:src="profesional.avatar"
										:alt="profesional.nombre"
										class="avatar-img"
									/>
									<div v-else class="avatar-fallback">
										{{ initials(profesional) }}
									</div>
								</div>

								<span class="especialidad-badge">
									{{ getEspecialidadLabel(profesional.especialidad) }}
								</span>

								<p class="profesional-display-name">{{ profesional.nombre }}</p>

								<div class="contacto-phone">
									<i class="fa-regular fa-phone"></i>
									<span>{{ profesional.contacto.telefono }}</span>
								</div>
								<p class="contacto-email">{{ profesional.contacto.email }}</p>

								<b-button
									variant="info"
									block
									class="mt-3"
									:disabled="!profesional.disponible"
								>
									<i class="fa-regular fa-comments mr-2"></i>
									Contactar
								</b-button>
							</router-link>
						</div>
					</div>

					<!-- Vacío -->
					<div v-if="!filteredProfesionales.length" class="empty-state text-center py-5">
						<i class="fa-regular fa-users empty-icon"></i>
						<h3 class="empty-title">No se encontraron profesionales</h3>
						<p class="empty-text">
							Intenta ajustar tus filtros de búsqueda o ampliar los criterios.
						</p>
						<b-button variant="outline-secondary" @click="clearFilters">
							Limpiar filtros
						</b-button>
					</div>
				</template>

				<!-- Estado inicial -->
				<div v-else class="empty-state text-center py-5" data-aos="fade-up" data-aos-delay="100">
					<i class="fa-regular fa-magnifying-glass empty-icon empty-icon--lg"></i>
					<h3 class="empty-title">Comienza tu búsqueda</h3>
					<p class="empty-text mb-0">
						Escribe en el campo de búsqueda para encontrar abogados y firmas especializadas
					</p>
				</div>

				<!-- CTA Lexi -->
				<div class="cta-card mt-5 text-center" data-aos="fade-up" data-aos-delay="50">
					<h3 class="cta-title">¿No encuentras lo que buscas?</h3>
					<p class="cta-text">
						Habla con Lexi, nuestra IA legal, para obtener recomendaciones personalizadas
					</p>
					<b-button to="/chat" variant="info">
						<i class="fa-regular fa-comments mr-2"></i>
						Consultar con Lexi
					</b-button>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
export default {
	name: 'DirectoryPage',
	data() {
		return {
			searchTerm: '',
			selectedEspecialidad: 'todas',
			showFirmas: true,
			showParticulares: true,
			ciudadFilter: '',
			isTyping: false,
			showAutocomplete: false,
			autocompleteResults: [],
			typingTimeout: null,
			especialidades: [
				{ value: 'todas', label: 'Todas las especialidades' },
				{ value: 'civil', label: 'Derecho Civil' },
				{ value: 'penal', label: 'Derecho Penal' },
				{ value: 'laboral', label: 'Derecho Laboral' },
				{ value: 'familiar', label: 'Derecho Familiar' },
				{ value: 'mercantil', label: 'Derecho Mercantil' },
				{ value: 'fiscal', label: 'Derecho Fiscal' },
				{ value: 'inmobiliario', label: 'Derecho Inmobiliario' },
				{ value: 'seguros', label: 'Derecho de Seguros' },
			],
			profesionalesData: [
				{
					id: '1',
					tipo: 'abogado',
					nombre: 'María Elena',
					apellidos: 'González Ruiz',
					especialidad: 'civil',
					disponible: true,
					verificado: true,
					descripcion:
						'Especialista en derecho civil con amplia experiencia en contratos, responsabilidad civil y derecho inmobiliario.',
					avatar: null,
					contacto: {
						telefono: '+52 55 1234 5678',
						email: 'maria.gonzalez@email.com',
					},
				},
				{
					id: '2',
					tipo: 'firma',
					nombre: 'Martínez & Asociados',
					especialidad: 'laboral',
					disponible: true,
					verificado: true,
					descripcion: 'Firma especializada en derecho laboral con más de 20 años de experiencia.',
					avatar: null,
					contacto: {
						telefono: '+52 55 1234 5678',
						email: 'contacto@martinezasociados.com',
					},
				},
				{
					id: '3',
					tipo: 'abogado',
					nombre: 'Carlos Alberto',
					apellidos: 'Mendoza López',
					especialidad: 'laboral',
					disponible: true,
					verificado: true,
					descripcion:
						'Abogado laboralista con experiencia en despidos injustificados, demandas laborales y negociación colectiva.',
					avatar: null,
					contacto: {
						telefono: '+52 55 9876 5432',
						email: 'carlos.mendoza@email.com',
					},
				},
				{
					id: '4',
					tipo: 'firma',
					nombre: 'López Legal Group',
					especialidad: 'fiscal',
					disponible: true,
					verificado: true,
					descripcion: 'Grupo legal especializado en derecho fiscal y tributario.',
					avatar: null,
					contacto: {
						telefono: '+52 55 9876 5432',
						email: 'info@lopezlegal.com',
					},
				},
				{
					id: '5',
					tipo: 'abogado',
					nombre: 'Ana Patricia',
					apellidos: 'Herrera Vega',
					especialidad: 'familiar',
					disponible: false,
					verificado: true,
					descripcion:
						'Especialista en derecho familiar con enfoque en divorcios, custodia de menores y pensiones alimenticias.',
					avatar: null,
					contacto: {
						telefono: '+52 55 5555 1234',
						email: 'ana.herrera@email.com',
					},
				},
				{
					id: '6',
					tipo: 'firma',
					nombre: 'García & Partners',
					especialidad: 'mercantil',
					disponible: true,
					verificado: true,
					descripcion: 'Firma de abogados especializada en derecho mercantil y corporativo.',
					avatar: null,
					contacto: {
						telefono: '+52 55 5555 1234',
						email: 'contacto@garciapartners.com',
					},
				},
			],
		};
	},
	computed: {
		shouldShowResults() {
			return this.searchTerm.trim().length > 0;
		},
		filteredProfesionales() {
			const term = this.searchTerm.toLowerCase();
			return this.profesionalesData.filter((profesional) => {
				const matchesSearch =
					profesional.nombre.toLowerCase().includes(term) ||
					(profesional.apellidos
						? profesional.apellidos.toLowerCase().includes(term)
						: false) ||
					profesional.descripcion.toLowerCase().includes(term);

				const matchesEspecialidad =
					this.selectedEspecialidad === 'todas' ||
					profesional.especialidad === this.selectedEspecialidad;

				const matchesTipo =
					(this.showFirmas && profesional.tipo === 'firma') ||
					(this.showParticulares && profesional.tipo === 'abogado');

				return matchesSearch && matchesEspecialidad && matchesTipo;
			});
		},
	},
	beforeDestroy() {
		if (this.typingTimeout) {
			clearTimeout(this.typingTimeout);
		}
	},
	methods: {
		fullName(profesional) {
			return profesional.apellidos
				? profesional.nombre + ' ' + profesional.apellidos
				: profesional.nombre;
		},
		initials(profesional) {
			const a = profesional.nombre.charAt(0);
			const b = profesional.apellidos ? profesional.apellidos.charAt(0) : '';
			return (a + b).toUpperCase();
		},
		getEspecialidadLabel(especialidad) {
			const found = this.especialidades.find((e) => e.value === especialidad);
			return found ? found.label : especialidad;
		},
		handleSearchChange() {
			const value = this.searchTerm;
			this.isTyping = true;
			if (this.typingTimeout) {
				clearTimeout(this.typingTimeout);
			}
			this.typingTimeout = setTimeout(() => {
				this.isTyping = false;
			}, 1000);

			if (value.trim().length > 0) {
				const suggestions = [];
				const seen = {};

				this.profesionalesData.forEach((profesional) => {
					const name = this.fullName(profesional);
					if (name.toLowerCase().includes(value.toLowerCase()) && !seen[name]) {
						seen[name] = true;
						suggestions.push(name);
					}

					const especialidadLabel = this.getEspecialidadLabel(profesional.especialidad);
					if (
						especialidadLabel.toLowerCase().includes(value.toLowerCase()) &&
						!seen[especialidadLabel]
					) {
						seen[especialidadLabel] = true;
						suggestions.push(especialidadLabel);
					}
				});

				this.autocompleteResults = suggestions.slice(0, 5);
				this.showAutocomplete = true;
			} else {
				this.showAutocomplete = false;
				this.autocompleteResults = [];
			}
		},
		handleAutocompleteSelect(suggestion) {
			this.searchTerm = suggestion;
			this.showAutocomplete = false;
		},
		onSearchFocus() {
			if (this.searchTerm.trim().length > 0) {
				this.showAutocomplete = true;
			}
		},
		onSearchBlur() {
			setTimeout(() => {
				this.showAutocomplete = false;
			}, 200);
		},
		clearFilters() {
			this.searchTerm = '';
			this.selectedEspecialidad = 'todas';
			this.showFirmas = true;
			this.showParticulares = true;
			this.ciudadFilter = '';
			this.autocompleteResults = [];
			this.showAutocomplete = false;
		},
	},
};
</script>
